/**
 * Model pricing (Configuration tab section) — every model's token prices, grouped like the model
 * picker. The overview row edits Standard (≤200K) input / output / cached rates;
 * "All tiers" opens the full Standard / Priority / Flex-Batch × context-band grid.
 * Defaults are the server catalog; edits apply to new estimates for this
 * browser session only (sent as per-request overrides, never persisted).
 */

import type { Currency, EstimationParams, ModelInfo } from '@tcc/shared';
import { useState } from 'react';
import {
	addNativePricing,
	type Band,
	displayPricing,
	FIELD_LABELS,
	FIELDS,
	type Field,
	isPriceEdited,
	removeNativePricing,
	resetPricing,
	setRate,
	type Tier,
} from '../../pricing';
import { modelGroupLabel } from '../ModelOptions/ModelOptions';
import { NumCell } from '../NumCell/NumCell';
import { Row } from '../Row/Row';
import { Section } from '../Section/Section';
import { TierGrid } from './TierGrid';

const CCY_SYMBOL: Record<Currency, string> = { GBP: '£', USD: '$' };

export interface ModelPricingProps {
	models: ModelInfo[];
	/** Server defaults, used for "Edited" markers and resets. */
	defaultModels: ModelInfo[];
	params: EstimationParams;
	onModelsChange: (models: ModelInfo[]) => void;
	onParamsChange: (params: EstimationParams) => void;
}

export function ModelPricing({ models, defaultModels, params, onModelsChange, onParamsChange }: ModelPricingProps) {
	const [ccy, setCcy] = useState<Currency>('USD');
	const [open, setOpen] = useState<Set<string>>(new Set());
	const fx = params.fxRateUsdToGbp;
	const defaults = new Map(defaultModels.map((m) => [m.modelId, m]));
	const editedCount = models.filter((m) => isPriceEdited(m, defaults.get(m.modelId))).length;

	const replace = (next: ModelInfo) => onModelsChange(models.map((m) => (m.modelId === next.modelId ? next : m)));
	const toggle = (id: string) =>
		setOpen((s) => {
			const n = new Set(s);
			n.has(id) ? n.delete(id) : n.add(id);
			return n;
		});
	const resetAll = () =>
		onModelsChange(
			models.map((m) => (defaults.has(m.modelId) ? resetPricing(m, defaults.get(m.modelId) as ModelInfo) : m)),
		);

	const groups = new Map<string, ModelInfo[]>();
	for (const m of models) groups.set(modelGroupLabel(m), [...(groups.get(modelGroupLabel(m)) ?? []), m]);

	return (
		<div className="prc-section">
			<Section
				badge="£"
				title="Model pricing"
				sub="Token prices per 1M tokens for each model. Defaults come from each vendor's published price list (AWS Bedrock models: London region). Changes apply to new estimates straight away, for this browser session only."
			/>

			<div className="prc-toolbar">
				<div className="prc-fx">
					<Row
						label="FX rate USD→GBP"
						step={0.01}
						value={fx}
						onChange={(v) => onParamsChange({ ...params, fxRateUsdToGbp: v })}
					/>
				</div>
				{/* biome-ignore lint/a11y/useSemanticElements: toggle buttons grouped by role="group" — <fieldset> is inappropriate here as these are not form inputs */}
				<div role="group" aria-label="Pricing currency" className="period-group prc-ccy">
					{(['USD', 'GBP'] as Currency[]).map((c) => (
						<button
							key={c}
							type="button"
							onClick={() => setCcy(c)}
							aria-pressed={ccy === c}
							className="period-btn period-btn--dark"
						>
							{CCY_SYMBOL[c]} {c}
							{c === 'USD' ? ' (base)' : ''}
						</button>
					))}
				</div>
				<div className="prc-reset">
					<span className="prc-count" aria-live="polite">
						{editedCount === 0 ? 'All prices at defaults' : `${editedCount} model${editedCount > 1 ? 's' : ''} edited`}
					</span>
					<button type="button" className="btns prc-btn" disabled={editedCount === 0} onClick={resetAll}>
						Reset all prices to defaults
					</button>
				</div>
			</div>
			{ccy !== 'USD' && (
				<p className="cfg-pricing-note">
					{ccy} figures in grey are USD base × FX {fx} and read-only. Models with native {ccy} vendor pricing are edited
					directly — open "All tiers" to add or remove native pricing.
				</p>
			)}

			{[...groups].map(([label, ms]) => (
				<section key={label} className="prc-group" aria-label={label}>
					<h3 className="prc-group-title">{label}</h3>
					<table className="prc-table">
						<thead>
							<tr>
								<th scope="col">Model</th>
								{FIELDS.map((f) => (
									<th key={f} scope="col" className="prc-num">
										{FIELD_LABELS[f]}
									</th>
								))}
								<th scope="col">
									<span className="sr-only">Details</span>
								</th>
							</tr>
						</thead>
						<tbody>
							{ms.map((m) => {
								const def = defaults.get(m.modelId);
								const edited = isPriceEdited(m, def);
								const { pricing, derived } = displayPricing(m, ccy, fx);
								const defPricing = def ? displayPricing(def, ccy, fx).pricing : pricing;
								const isChanged = (t: Tier, b: Band, f: Field) => pricing[t][b][f] !== defPricing[t][b][f];
								const set = (t: Tier, b: Band, f: Field, v: number) => replace(setRate(m, ccy, t, b, f, v));
								const isOpen = open.has(m.modelId);
								const hasNative = ccy !== 'USD' && m.pricingByCurrency?.[ccy] !== undefined;
								return [
									<tr key={m.modelId} className={edited ? 'prc-edited' : undefined}>
										<th scope="row" className="prc-name">
											{m.displayName}
											{edited && <span className="prc-tag">Edited</span>}
										</th>
										{FIELDS.map((f) => (
											<td key={f} className={`prc-num${isChanged('standard', 'le200k', f) ? ' prc-changed' : ''}`}>
												{derived ? (
													<span className="cfg-derived">{pricing.standard.le200k[f]}</span>
												) : (
													<NumCell
														value={pricing.standard.le200k[f]}
														step={0.001}
														label={`${m.displayName} · Standard · ${FIELD_LABELS[f]}`}
														onChange={(v) => set('standard', 'le200k', f, v)}
													/>
												)}
											</td>
										))}
										<td className="prc-act">
											<button
												type="button"
												className="prc-link"
												aria-expanded={isOpen}
												aria-controls={`prc-${m.modelId}`}
												onClick={() => toggle(m.modelId)}
											>
												{isOpen ? 'Hide tiers' : 'All tiers'}
											</button>
										</td>
									</tr>,
									isOpen && (
										<tr key={`${m.modelId}-detail`} id={`prc-${m.modelId}`} className="prc-detail">
											<td colSpan={FIELDS.length + 2}>
												<TierGrid
													name={m.displayName}
													pricing={pricing}
													readOnly={derived}
													isChanged={isChanged}
													onChange={set}
												/>
												<div className="prc-detail-actions">
													{ccy !== 'USD' &&
														(hasNative ? (
															<button
																type="button"
																className="btns prc-btn"
																onClick={() => replace(removeNativePricing(m, ccy))}
															>
																Remove native {ccy} pricing (use FX)
															</button>
														) : (
															<button
																type="button"
																className="btns prc-btn"
																onClick={() => replace(addNativePricing(m, ccy, fx))}
															>
																Add native {ccy} vendor pricing
															</button>
														))}
													<button
														type="button"
														className="btns prc-btn"
														disabled={!edited || !def}
														onClick={() => def && replace(resetPricing(m, def))}
													>
														Reset {m.displayName} to default
													</button>
												</div>
											</td>
										</tr>
									),
								];
							})}
						</tbody>
					</table>
				</section>
			))}
			<p className="cfg-pricing-footer">
				Overview columns are the Standard tier for requests up to 200K input tokens; "All tiers" shows Priority, Flex /
				Batch and the over-200K band. Amounts in {CCY_SYMBOL[ccy]}
				{ccy} per 1M tokens.
			</p>
		</div>
	);
}
